type IsolationMode = "lock" | "contain";

type RegisteredRoot = {
  element: HTMLElement;
  mode: IsolationMode;
};

type SavedInlineStyle = {
  overflow: string;
  overflowX: string;
  overflowY: string;
  overscrollBehavior: string;
  overscrollBehaviorY: string;
  paddingRight: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
};

const APP_SCROLL_CONTAINER_SELECTOR = ".pwa-scroll-container";

const registeredRoots = new Set<RegisteredRoot>();
let listenerCount = 0;
let lockCount = 0;
let touchStartX = 0;
let touchStartY = 0;

let savedHtml: Pick<SavedInlineStyle, "overflow" | "overflowY" | "overscrollBehaviorY"> | null = null;
let savedBody: SavedInlineStyle | null = null;
let savedAppScroll: Pick<SavedInlineStyle, "overflow" | "overflowY" | "overscrollBehaviorY"> | null = null;
let savedWindowScrollY = 0;
let savedAppScrollTop = 0;

function snapshotStyle(el: HTMLElement): SavedInlineStyle {
  return {
    overflow: el.style.overflow,
    overflowX: el.style.overflowX,
    overflowY: el.style.overflowY,
    overscrollBehavior: el.style.overscrollBehavior,
    overscrollBehaviorY: el.style.overscrollBehaviorY,
    paddingRight: el.style.paddingRight,
    position: el.style.position,
    top: el.style.top,
    left: el.style.left,
    right: el.style.right,
    width: el.style.width,
  };
}

function isElement(target: EventTarget | null): target is Element {
  return Boolean(target && target instanceof Element);
}

function isInteractiveMediaTarget(target: EventTarget | null): boolean {
  if (!isElement(target)) return false;
  const el = target instanceof HTMLElement ? target : target.parentElement;
  if (!el) return false;
  if (el.closest("video, audio, input, textarea, select, [contenteditable='true']")) {
    return true;
  }
  return el.isContentEditable;
}

function canScroll(el: HTMLElement, dx: number, dy: number): boolean {
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  const overflowX = style.overflowX;
  const canY = overflowY === "auto" || overflowY === "scroll";
  const canX = overflowX === "auto" || overflowX === "scroll";

  if (canY && Math.abs(dy) >= Math.abs(dx)) {
    if (dy < 0 && el.scrollTop > 0) return true;
    if (dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
  }

  if (canX && Math.abs(dx) > 0) {
    if (dx < 0 && el.scrollLeft > 0) return true;
    if (dx > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 1) return true;
  }

  return false;
}

function findScrollableWithin(start: EventTarget | null, root: HTMLElement): HTMLElement | null {
  if (!isElement(start) || !root.contains(start)) return null;

  let current: Element | null = start;
  while (current && current !== root) {
    if (current instanceof HTMLElement) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const scrollsY = (overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight;
      const scrollsX = (overflowX === "auto" || overflowX === "scroll") && current.scrollWidth > current.clientWidth;
      if (scrollsY || scrollsX) {
        return current;
      }
    }
    current = current.parentElement;
  }

  if (root instanceof HTMLElement) {
    const style = window.getComputedStyle(root);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const scrollsY = (overflowY === "auto" || overflowY === "scroll") && root.scrollHeight > root.clientHeight;
    const scrollsX = (overflowX === "auto" || overflowX === "scroll") && root.scrollWidth > root.clientWidth;
    if (scrollsY || scrollsX) {
      return root;
    }
  }

  return null;
}

function hasLockRoots(): boolean {
  for (const root of registeredRoots) {
    if (root.mode === "lock") return true;
  }
  return false;
}

function shouldAllowScroll(
  target: EventTarget | null,
  dx: number,
  dy: number,
  kind: "wheel" | "touch"
): boolean {
  if (hasLockRoots()) {
    for (const root of registeredRoots) {
      if (root.mode !== "lock") continue;
      if (!isElement(target) || !root.element.contains(target)) continue;
      const scroller = findScrollableWithin(target, root.element);
      if (scroller && canScroll(scroller, dx, dy)) return true;
      // Keep native video/input gestures working; page scroll stays locked separately.
      if (kind === "touch" && isInteractiveMediaTarget(target) && !scroller) return true;
    }
    return false;
  }

  for (const root of registeredRoots) {
    if (root.mode !== "contain") continue;
    if (!isElement(target) || !root.element.contains(target)) continue;
    const scroller = findScrollableWithin(target, root.element);
    if (scroller && canScroll(scroller, dx, dy)) return true;
    return false;
  }

  return true;
}

function onTouchStart(event: TouchEvent): void {
  if (event.touches.length !== 1) return;
  touchStartX = event.touches[0].clientX;
  touchStartY = event.touches[0].clientY;
}

function onWheel(event: WheelEvent): void {
  if (registeredRoots.size === 0) return;
  if (shouldAllowScroll(event.target, event.deltaX, event.deltaY, "wheel")) return;
  event.preventDefault();
}

function onTouchMove(event: TouchEvent): void {
  if (registeredRoots.size === 0) return;
  if (event.touches.length !== 1) return;

  const dx = -(event.touches[0].clientX - touchStartX);
  const dy = -(event.touches[0].clientY - touchStartY);
  if (shouldAllowScroll(event.target, dx, dy, "touch")) return;
  event.preventDefault();
}

function addDocumentListeners(): void {
  document.addEventListener("wheel", onWheel, { passive: false, capture: true });
  document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
  document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
}

function removeDocumentListeners(): void {
  document.removeEventListener("wheel", onWheel, true);
  document.removeEventListener("touchstart", onTouchStart, true);
  document.removeEventListener("touchmove", onTouchMove, true);
}

function acquireBackgroundScrollLock(): void {
  if (lockCount === 0) {
    const html = document.documentElement;
    const body = document.body;
    const appScroll = document.querySelector<HTMLElement>(APP_SCROLL_CONTAINER_SELECTOR);
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    savedWindowScrollY = window.scrollY || window.pageYOffset || 0;
    savedAppScrollTop = appScroll?.scrollTop ?? 0;
    savedHtml = {
      overflow: html.style.overflow,
      overflowY: html.style.overflowY,
      overscrollBehaviorY: html.style.overscrollBehaviorY,
    };
    savedBody = snapshotStyle(body);
    savedAppScroll = appScroll
      ? {
          overflow: appScroll.style.overflow,
          overflowY: appScroll.style.overflowY,
          overscrollBehaviorY: appScroll.style.overscrollBehaviorY,
        }
      : null;

    html.style.overflow = "hidden";
    html.style.overflowY = "hidden";
    html.style.overscrollBehaviorY = "none";

    body.style.position = "fixed";
    body.style.top = `-${savedWindowScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehaviorY = "none";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    if (appScroll) {
      appScroll.style.overflow = "hidden";
      appScroll.style.overflowY = "hidden";
      appScroll.style.overscrollBehaviorY = "none";
    }
  }

  lockCount += 1;
}

function releaseBackgroundScrollLock(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;

  const html = document.documentElement;
  const body = document.body;
  const appScroll = document.querySelector<HTMLElement>(APP_SCROLL_CONTAINER_SELECTOR);

  if (savedHtml) {
    html.style.overflow = savedHtml.overflow;
    html.style.overflowY = savedHtml.overflowY;
    html.style.overscrollBehaviorY = savedHtml.overscrollBehaviorY;
    savedHtml = null;
  }

  if (savedBody) {
    body.style.overflow = savedBody.overflow;
    body.style.overflowY = savedBody.overflowY;
    body.style.overscrollBehavior = savedBody.overscrollBehavior;
    body.style.overscrollBehaviorY = savedBody.overscrollBehaviorY;
    body.style.paddingRight = savedBody.paddingRight;
    body.style.position = savedBody.position;
    body.style.top = savedBody.top;
    body.style.left = savedBody.left;
    body.style.right = savedBody.right;
    body.style.width = savedBody.width;
    savedBody = null;
  }

  if (appScroll && savedAppScroll) {
    appScroll.style.overflow = savedAppScroll.overflow;
    appScroll.style.overflowY = savedAppScroll.overflowY;
    appScroll.style.overscrollBehaviorY = savedAppScroll.overscrollBehaviorY;
    appScroll.scrollTop = savedAppScrollTop;
    savedAppScroll = null;
  }

  window.scrollTo(0, savedWindowScrollY);
}

export function isolateOverlayScroll(element: HTMLElement, mode: IsolationMode = "lock"): () => void {
  const entry: RegisteredRoot = { element, mode };
  registeredRoots.add(entry);

  if (listenerCount === 0) {
    addDocumentListeners();
  }
  listenerCount += 1;

  if (mode === "lock") {
    acquireBackgroundScrollLock();
  }

  return () => {
    registeredRoots.delete(entry);
    listenerCount = Math.max(0, listenerCount - 1);
    if (listenerCount === 0) {
      removeDocumentListeners();
    }
    if (mode === "lock") {
      releaseBackgroundScrollLock();
    }
  };
}

export type { IsolationMode };
